import React from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {useConfig} from '@lore/config';
import {
  Accordion,
  InContextHelpMessage,
  LinkButton,
  ListSelect,
  Loader,
  ProjectSharingSuggestions,
  SocialShareButtons
} from '@communityshare/ui-shared';
import {PayloadStates} from '@lore/utils';
import DialogTemplate from '@/dialogs_common/DialogTemplate';
import CloseDialogButton from '@/dialogs_common/CloseDialogButton';
import LinkPreview from './LinkPreview';
import {useConnect} from "@lore/query-connect";
import getProjectShareUrl from '@/features/share-project/utils/getProjectShareUrl';

ProjectShareDialog.propTypes = {
  project: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired
};

export default function ProjectShareDialog(props) {
  const {project, onClose} = props;
  const {t} = useTranslation();
  const config = useConfig();
  const network = useConnect('network.byId', {
    id: project?.data?.networkId
  }, {enabled: !!project?.data?.networkId});

  if (project.state === PayloadStates.FETCHING || network.state === PayloadStates.FETCHING) {
    return (
      <DialogTemplate>
        <Loader/>
      </DialogTemplate>
    );
  }

  const shareUrl = getProjectShareUrl(config, project);

  function onEmail() {
    window.location.href = `mailto:?subject=${encodeURIComponent(project.data.title)}&body=${encodeURIComponent(shareUrl)}`;
  }

  function onLinkedIn() {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank');
  }

  function onFacebook() {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
  }

  function onCopyLink() {
    navigator.clipboard.writeText(shareUrl);
  }

  return (
    <DialogTemplate className="w-screen max-w-screen-lg !bg-cs-gray-100">
      <CloseDialogButton onClick={onClose}/>
      <div className="px-6 pt-6 pb-8 sm:px-8">
        <p className="text-base text-cs-gray-500">
          <span className="text-cs-gray-900">{t('project_share.breadcrumb_action')}</span>
        </p>
        <div className="mt-5">
          <LinkButton
            label={t('project_share.back_to_project')}
            icon="left"
            onClick={onClose}
          />
        </div>
        <h2 className="mt-2 text-3xl font-semibold text-cs-gray-900">
          {t('project_share.title')}
        </h2>
        <p className="mt-2 text-base text-cs-gray-500">
          {t('project_share.subtitle')}
        </p>
        <div className="mt-6 flex flex-col gap-1">
          <label className="text-base text-cs-gray-900">
            {t('project_share.field_label')}
          </label>
          <ListSelect
            value={project}
            options={[project.data]}
            disabled={true}
            getOptionLabel={() => project.data.title || 'INCOMPLETE'}
            getOptionKey={() => project.id}
            className="bg-white"
            onChange={() => {}}
          />
        </div>
        <div className="mt-4">
          <InContextHelpMessage
            boldText={t('project_share.callout_bold')}
            bodyText={t('project_share.callout_body')}
          />
        </div>
        <div className="mt-6">
          <LinkPreview project={project}/>
        </div>
        <div className="mt-4">
          <Accordion>
            <ProjectSharingSuggestions
              options={[
                {
                  key: 'find_partners',
                  label: t('project_share.option_find_partners'),
                  template: t('project_share.template_find_partners', {guidingQuestion: project.data.title || 'INCOMPLETE'})
                },
                {
                  key: 'spread_word',
                  label: t('project_share.option_spread_word', {brand: network?.data?.name || t('project_share.brand')}),
                  template: t('project_share.template_spread_word', {brand: network?.data?.name || t('project_share.brand')})
                }
              ]}
              examples={[
                {id: 'example-1', text: t('project_share.example_1')},
                {id: 'example-2', text: t('project_share.example_2')},
              ]}
              helpTitle={t('project_share.help_title')}
              viewSuggestionsLabel={t('project_share.view_suggestions')}
              helpQuestion={t('project_share.help_question')}
              templateLabel={t('project_share.template_label')}
              resetTemplateLabel={t('project_share.reset_template')}
              copyLabel={t('project_share.copy_text')}
              copiedLabel={t('project_share.copied')}
              seeExamplesLabel={t('project_share.see_examples')}
            />
          </Accordion>
        </div>
        <div className="mt-6">
          <SocialShareButtons
            onEmail={onEmail}
            onLinkedIn={onLinkedIn}
            onFacebook={onFacebook}
            onCopyLink={onCopyLink}
            copiedLinkLabel={t('controls.Copied')}
            copyLinkLabel={t('controls.Copy link')}
          />
        </div>
      </div>
    </DialogTemplate>
  );
}
